import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { QueryState } from "@/shared/ui/QueryState";
import { Badge, Button, Card, SimplePagination } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import { ChevronRight, ClipboardList, Eye } from "lucide-react";
import { useEffect } from "react";
import {
  useInternalAuditPlansQuery,
  useInternalAuditRecordsQuery,
} from "../api/queries";

function overallBadge(result: "pass" | "fail" | "needs_improvement") {
  if (result === "pass")
    return (
      <Badge variant="secondary" className="bg-emerald-600/15 text-emerald-800">
        ຜ່ານ
      </Badge>
    );
  if (result === "fail") return <Badge variant="destructive">ບໍ່ຜ່ານ</Badge>;
  return (
    <Badge variant="secondary" className="bg-amber-500/15 text-amber-900">
      ຕ້ອງປັບປຸງ
    </Badge>
  );
}

const PAGE_SIZE = 10;

export function InternalAuditListPage() {
  const nav = useNavigate({ from: "/app/internal-audit" });
  const search = useSearch({ from: "/app/internal-audit" });
  const page = search.page;
  const offset = (page - 1) * PAGE_SIZE;

  const plansQuery = useInternalAuditPlansQuery({ limit: 100, offset: 0 });
  const recordsQuery = useInternalAuditRecordsQuery({
    limit: PAGE_SIZE,
    offset,
  });

  const total = recordsQuery.data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const rows = recordsQuery.data?.data ?? [];

  useEffect(() => {
    if (!recordsQuery.isSuccess) return;
    if (total === 0 && page > 1) {
      nav({ search: { ...search, page: 1 }, replace: true });
      return;
    }
    if (total > 0 && page > totalPages) {
      nav({ search: { ...search, page: totalPages }, replace: true });
    }
  }, [recordsQuery.isSuccess, page, totalPages, total, nav, search]);

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6 p-4 md:p-6">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-8 w-8 text-primary" />
          <h1 className="font-semibold text-2xl">
            ບັນທຶກການກວດສອບພາຍໃນ (ຕາມຕຳແໜ່ງ)
          </h1>
        </div>

        <p className="text-muted-foreground text-sm">
          ສະແດງສະເພາະບັນທຶກທີ່ແຜນກຳນົດໃຫ້ຕຳແໜ່ງຜູ້ກວດຕົງກັບຕຳແໜ່ງຂອງທ່ານໃນໂຄງຮ່າງອົງກອນ.
        </p>

        <section className="space-y-3">
          <h2 className="font-medium text-lg">ເລືອກແຜນຕວດເພື່ອບັນທຶກການກວດ</h2>
          {plansQuery.isLoading ? (
            <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດແຜນ...</p>
          ) : plansQuery.isError ? (
            <p className="text-destructive text-sm">ໂຫຼດແຜນບໍ່ສຳເລັດ</p>
          ) : !plansQuery.data?.data.length ? (
            <Card className="border-border p-4">
              <p className="text-muted-foreground text-sm">
                ຍັງບໍ່ມີແຜນຕວດທີ່ກຳນົດຕຳແໜ່ງຜູ້ກວດຕົງກັບທ່ານ.
              </p>
            </Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-3">
              {plansQuery.data.data.map((p) => (
                <Card key={p.id} className="border-border p-4">
                  <div className="flex flex-col gap-2">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-muted-foreground text-xs">
                      {p.auditTypeName} · {p.departmentName}
                      {p.qcStandardName ? ` · ${p.qcStandardName}` : ""}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 w-fit"
                      onClick={() => {
                        nav({
                          to: "/app/internal-audit/plan/$planId",
                          params: { planId: p.id },
                        });
                      }}
                    >
                      ເລີ່ມບັນທຶກ
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        <section className="space-y-3">
          <h2 className="font-medium text-lg">ບັນທຶກກ່ອນໜ້ານີ້</h2>
          {recordsQuery.isLoading ? (
            <QueryState isLoading>ກຳລັງໂຫຼດ...</QueryState>
          ) : recordsQuery.isError ? (
            <QueryState isError error={recordsQuery.error}>
              ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ
            </QueryState>
          ) : !rows.length ? (
            <Card className="border-border p-6">
              <p className="text-muted-foreground text-sm">
                {total === 0
                  ? "ຍັງບໍ່ມີບັນທຶກ ຫຼື ບັນຊີຂອງທ່ານບໍ່ມີຕຳໜິງກຳລັງດຳເນີນຢູ່ ຫຼື ບໍ່ມີແຜນຕວດທີ່ກຳນົດຕຳແໜ່ງຜູ້ກວດຕົງກັບທ່ານ."
                  : "—"}
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">ວັນທີ</th>
                      <th className="px-3 py-2 text-left font-medium">ແຜນ</th>
                      <th className="px-3 py-2 text-left font-medium">ປະເພດ</th>
                      <th className="px-3 py-2 text-left font-medium">ຜູ້ຕວດ</th>
                      <th className="px-3 py-2 text-left font-medium">ຜົນ</th>
                      <th className="px-3 py-2 text-right font-medium">
                        ລາຍລະອຽດ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-border/60 border-b">
                        <td className="whitespace-nowrap px-3 py-2">
                          {format(new Date(r.auditDate), "yyyy-MM-dd HH:mm")}
                        </td>
                        <td className="max-w-[12rem] truncate px-3 py-2">
                          {r.planName}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {r.auditTypeName}
                          {r.qcStandardName ? ` · ${r.qcStandardName}` : ""}
                        </td>
                        <td className="max-w-[10rem] truncate px-3 py-2">
                          {r.auditorName?.trim() || r.auditorId}
                        </td>
                        <td className="px-3 py-2">
                          {overallBadge(r.overallResult)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-1"
                            onClick={() => {
                              nav({
                                to: "/app/internal-audit/record/$recordId",
                                params: { recordId: r.id },
                              });
                            }}
                          >
                            <Eye className="size-3.5" aria-hidden />
                            ເບິ່ງ
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 ? (
                <div className="flex flex-col items-center gap-2">
                  <SimplePagination
                    page={safePage}
                    totalPages={totalPages}
                    onPageChange={(p) => nav({ search: { ...search, page: p } })}
                  />
                  <p className="text-center text-muted-foreground text-xs">
                    ໜ້າ {safePage} ຈາກ {totalPages} · ທັງໝົດ {total} ບັນທຶກ
                  </p>
                </div>
              ) : (
                <p className="text-center text-muted-foreground text-xs">
                  {total} ບັນທຶກ
                </p>
              )}
            </div>
          )}
        </section>
      </Main>
    </>
  );
}
